import { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { zodResolver } from '@hookform/resolvers/zod';
import type { TossPaymentsWidgets } from '@tosspayments/tosspayments-sdk';
import {
  BookOpen,
  Building2,
  Calendar,
  CheckCircle2,
  CreditCard,
  Loader2,
  RotateCcw,
  Search,
  SearchX,
  UserRound,
} from 'lucide-react';
import { z } from 'zod';

import {
  createTossWidgets,
  renderTossWidgets,
  requestTossWidgetPayment,
  TOSS_AGREEMENT_ID,
  TOSS_PAYMENT_METHOD_ID,
} from '../../lib/toss';
import { useGetCourseByCode, useJoinCourseByCode } from '../../query/course';
import { usePreparePayment } from '../../query/payment';
import InputField from '../fields/input-field';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Form } from '../ui/form';
import { Separator } from '../ui/separator';

const formSchema = z.object({
  accessCode: z
    .string({ required_error: '강좌코드를 입력해주세요.' })
    .min(10, '강좌코드는 10자 이상이어야 합니다.'),
});

type FormData = z.infer<typeof formSchema>;

interface JoinCourseModalProps {
  onClose: () => void;
}
const JoinCourseModal = (props: JoinCourseModalProps) => {
  const { onClose } = props;

  const [searchedCode, setSearchedCode] = useState<string | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accessCode: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { data: course, isFetching } = useGetCourseByCode(searchedCode ?? '', {
    queryKey: ['student', 'course', searchedCode],
    enabled: !!searchedCode,
  });
  const { mutate: joinCourseByCode, isPending } = useJoinCourseByCode();
  const { mutate: preparePayment, isPending: isPreparing } = usePreparePayment();
  const queryClient = useQueryClient();

  // 결제 단계(3단계): 주문 생성 후 결제위젯(결제수단 선택 + 약관)을 화면에 렌더한다.
  const [payment, setPayment] = useState<{
    orderId: string;
    orderName: string;
    amount: number;
  } | null>(null);
  const widgetsRef = useRef<TossPaymentsWidgets | null>(null);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  const onSubmit = (data: FormData) => {
    setSearchedCode(data.accessCode);
  };

  const handleJoin = () => {
    if (!searchedCode) return;
    joinCourseByCode(searchedCode, {
      onSuccess: () => {
        toast.success('강좌에 참여했습니다.');
        queryClient.invalidateQueries({
          queryKey: ['student', 'course'],
        });
        onClose();
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        toast.error(error.message ?? '강좌 참여에 실패했습니다.');
      },
    });
  };

  /** Toss SDK 에러를 사용자에게 보여줄 문구로 만든다. (취소면 null) */
  const describeTossError = (error: unknown, fallback: string): string | null => {
    const e = error as { code?: string; message?: string };
    if (e?.code === 'USER_CANCEL' || e?.code === 'PAY_PROCESS_CANCELED') return null;
    console.error('[payment] 실패', { code: e?.code, message: e?.message, error });
    if (!e?.code && !e?.message) return fallback;
    return `${fallback} ${e.message ?? ''}${e.code ? ` (${e.code})` : ''}`.trim();
  };

  // 유료 강좌 1단계: 주문 생성 → 결제 단계로 전환(위젯 렌더는 아래 useEffect에서).
  const handlePay = () => {
    if (!searchedCode) return;
    preparePayment(
      { accessCode: searchedCode },
      {
        onSuccess: res => {
          if (!res.data) {
            toast.error('결제 준비에 실패했습니다.');
            return;
          }
          setPayment({
            orderId: res.data.orderId,
            orderName: res.data.orderName,
            amount: res.data.amount,
          });
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          toast.error(error.message ?? '결제 준비에 실패했습니다.');
        },
      },
    );
  };

  // 결제 단계 진입 시 위젯 생성 → 결제수단·약관 UI 렌더. (셀렉터 대상 div가 먼저 그려져야 하므로 effect에서)
  useEffect(() => {
    if (!payment) return;
    let cancelled = false;

    (async () => {
      try {
        const widgets = await createTossWidgets(payment.amount);
        if (cancelled) return;
        widgetsRef.current = widgets;
        await renderTossWidgets(widgets);
        if (!cancelled) setIsWidgetReady(true);
      } catch (error: unknown) {
        if (cancelled) return;
        toast.error(describeTossError(error, '결제 수단을 불러오지 못했습니다.') ?? '');
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment]);

  // 유료 강좌 2단계: 선택한 결제수단으로 결제 요청 → 성공 시 /payment/success로 이동해 승인·참여 처리.
  const handleConfirmPay = async () => {
    if (!widgetsRef.current || !payment) return;
    try {
      setIsPaying(true);
      await requestTossWidgetPayment(widgetsRef.current, {
        orderId: payment.orderId,
        orderName: payment.orderName,
      });
    } catch (error: unknown) {
      setIsPaying(false);
      const message = describeTossError(error, '결제에 실패했습니다.');
      if (message === null) {
        toast.info('결제를 취소했습니다.');
        return;
      }
      toast.error(message);
    }
  };

  const resetPayment = () => {
    setPayment(null);
    widgetsRef.current = null;
    setIsWidgetReady(false);
    setIsPaying(false);
  };

  const handleReset = () => {
    resetPayment();
    setSearchedCode(null);
    form.setFocus('accessCode');
  };

  const courseData = course?.data;
  const isSearched = searchedCode !== null && !isFetching;
  const isPaid = (courseData?.price ?? 0) > 0;
  const isPaymentStep = payment !== null;

  const step = isPaymentStep ? 3 : !isSearched ? 1 : 2;

  return (
    <div className="w-[440px] max-w-[92vw]">
      <div className="flex items-center gap-2 mb-1">
        <div
          className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${step >= 1 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
        >
          1
        </div>
        <span className={`text-sm ${step === 1 ? 'font-medium' : 'text-muted-foreground'}`}>
          코드 입력
        </span>
        <Separator className="w-6" />
        <div
          className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${step >= 2 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
        >
          2
        </div>
        <span className={`text-sm ${step === 2 ? 'font-medium' : 'text-muted-foreground'}`}>
          강좌 확인
        </span>
        {/* 결제 단계는 유료 강좌에서만 나타난다. */}
        {(isPaid || isPaymentStep) && (
          <>
            <Separator className="w-6" />
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${step >= 3 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
            >
              3
            </div>
            <span className={`text-sm ${step === 3 ? 'font-medium' : 'text-muted-foreground'}`}>
              결제
            </span>
          </>
        )}
      </div>

      <Separator className="mb-4" />

      {!isPaymentStep && (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
          <InputField
            control={form.control}
            name="accessCode"
            placeholder="교수님께 받은 강좌 코드를 입력해주세요."
            label="강좌 코드"
            type="text"
          />

          {!isSearched && (
            <div className="flex gap-3 justify-end">
              <Button type="button" onClick={onClose} variant={'outline'} className="min-w-[80px]">
                닫기
              </Button>
              <Button type="submit" className="min-w-[100px]" disabled={isFetching}>
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    조회 중...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4" />
                    강좌 조회
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </Form>
      )}

      {isSearched && !courseData && !isPaymentStep && (
        <div className="flex flex-col gap-4 mt-4">
          <div className="rounded-lg border border-dashed p-6 flex flex-col items-center gap-2 text-muted-foreground">
            <SearchX className="w-10 h-10 mb-1" />
            <p className="text-sm font-medium">해당 코드의 강좌가 없습니다.</p>
            <p className="text-xs">코드를 다시 확인하거나 교수님께 문의해주세요.</p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button type="button" onClick={onClose} variant={'outline'} className="min-w-[80px]">
              닫기
            </Button>
            <Button type="button" onClick={handleReset}>
              <RotateCcw className="w-4 h-4" />
              다시 입력
            </Button>
          </div>
        </div>
      )}

      {isSearched && courseData && !isPaymentStep && (
        <div className="flex flex-col gap-4 mt-4">
          <div className="rounded-lg border bg-muted/30 p-4 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary" />
              <span className="text-lg font-semibold">{courseData.name}</span>
            </div>
            <Separator />
            <div className="flex flex-col gap-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Building2 className="w-4 h-4" />
                <span>
                  {courseData.school} {courseData.department}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>
                  {courseData.year}년 {courseData.semester}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <UserRound className="w-4 h-4" />
                <span>담당 교수: </span>
                <Badge variant="secondary">{courseData.professor.name}</Badge>
              </div>
              {/* 유료 강좌는 결제 전에 구매자가 금액을 인지할 수 있어야 한다. (무료는 가격 줄 자체를 숨김) */}
              {courseData.price > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CreditCard className="w-4 h-4" />
                  <span>수강료: </span>
                  <span className="font-semibold text-foreground">
                    {courseData.price.toLocaleString()}원
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              onClick={handleReset}
              variant={'outline'}
              className="min-w-[80px]"
              disabled={isPreparing}
            >
              <RotateCcw className="w-4 h-4" />
              다시 입력
            </Button>
            {courseData.price > 0 ? (
              <Button
                type="button"
                onClick={handlePay}
                disabled={isPreparing}
                className="min-w-[140px]"
              >
                {isPreparing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    결제 준비 중...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4" />
                    결제하고 참여하기
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleJoin}
                disabled={isPending}
                className="min-w-[100px]"
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    참여 중...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    참여하기
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* 3단계: 결제위젯 — 결제수단 선택 + 약관 동의 후 결제 요청 */}
      {isPaymentStep && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-4 py-3">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{payment.orderName}</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs text-muted-foreground">결제 금액</span>
              <span className="text-lg font-bold">{payment.amount.toLocaleString()}원</span>
            </div>
          </div>

          <div className="max-h-[52vh] overflow-y-auto -mx-1 px-1">
            {/* Toss 위젯이 이 요소들 안에 결제 UI를 그린다. (셀렉터는 lib/toss.ts와 공유) */}
            <div id={TOSS_PAYMENT_METHOD_ID} />
            <div id={TOSS_AGREEMENT_ID} />
          </div>

          {!isWidgetReady && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              결제 수단을 불러오는 중...
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              onClick={resetPayment}
              variant={'outline'}
              className="min-w-[80px]"
              disabled={isPaying}
            >
              <RotateCcw className="w-4 h-4" />
              뒤로
            </Button>
            <Button
              type="button"
              onClick={handleConfirmPay}
              disabled={!isWidgetReady || isPaying}
              className="min-w-[120px]"
            >
              {isPaying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  결제 중...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  결제하기
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JoinCourseModal;
